import { useState } from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceTypes = [
    "RERA Registration",
    "Extension Application",
    "Correction Application",
    "De-registration",
    "Project Closure / Bank Account Closure",
    "Legal Compliance",
    "Documentation Services",
    "Business Advisory",
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! Your message has been sent successfully. We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_type: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen fade-in" data-testid="contact-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 section-light" data-testid="contact-hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <h1
            className="text-5xl md:text-6xl tracking-tight leading-tight text-[#1A1A1A] mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="contact-hero-heading"
          >
            Get In Touch
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-[#555555] max-w-3xl">
            Have questions? We're here to help. Reach out to us and let's discuss how we can assist your RERA registration and compliance needs.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20 md:py-32 section-light" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2
                  className="text-3xl md:text-4xl tracking-tight leading-snug text-[#1A1A1A] mb-8"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  data-testid="contact-info-heading"
                >
                  Our Offices
                </h2>
              </div>

              {/* Nashik Office */}
              <div className="space-y-6 pb-8 border-b border-slate-200">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-4">Nashik Office</h3>
                
                <div className="flex items-start gap-4" data-testid="contact-address-nashik">
                  <MapPin className="w-6 h-6 text-[#C9A14A] mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A] mb-1">Address</h4>
                    <p className="text-[#555555] leading-relaxed">
                      Office No 2, Shri Balaji Heights,<br />
                      Ravi Shankar Marg,<br />
                      Kalpataru Nagar,<br />
                      Nashik - 422011
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-phone-nashik">
                  <Phone className="w-6 h-6 text-[#C9A14A] mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A] mb-1">Phone</h4>
                    <a
                      href="tel:+917758882796"
                      className="text-[#555555] hover:text-[#C9A14A] transition-colors duration-300 block"
                    >
                      +91-77588 82796
                    </a>
                  </div>
                </div>
              </div>

              {/* Pune Office */}
              <div className="space-y-6 pb-8 border-b border-slate-200">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-4">Pune Office</h3>
                
                <div className="flex items-start gap-4" data-testid="contact-address-pune">
                  <MapPin className="w-6 h-6 text-[#C9A14A] mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A] mb-1">Address</h4>
                    <p className="text-[#555555] leading-relaxed">
                      Office No 1002/26, 2nd floor, Saakar building, opp. Sachin Tendulkar park, Navi peth, Pune - 411030
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-phone-pune">
                  <Phone className="w-6 h-6 text-[#C9A14A] mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A] mb-1">Phone</h4>
                    <a
                      href="tel:+919404515431"
                      className="text-[#555555] hover:text-[#C9A14A] transition-colors duration-300"
                    >
                      +91-9404515431
                    </a>
                  </div>
                </div>
              </div>

              {/* General Contact */}
              <div className="space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-email">
                  <Mail className="w-6 h-6 text-[#C9A14A] mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A] mb-1">Email</h4>
                    <a
                      href="mailto:hub.rera@gmail.com"
                      className="text-[#555555] hover:text-[#C9A14A] transition-colors duration-300"
                    >
                      hub.rera@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-hours">
                  <Clock className="w-6 h-6 text-[#C9A14A] mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A] mb-1">Business Hours</h4>
                    <p className="text-[#555555]">
                      Monday - Saturday: 9:00 AM - 6:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form
                onSubmit={handleSubmit}
                className="bg-white p-10 rounded-lg shadow-sm border border-slate-100"
                data-testid="contact-form"
              >
                <h3
                  className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-8"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Send Us a Message
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-slate-300 focus:border-[#C9A14A] focus:ring-1 focus:ring-[#C9A14A] rounded-md p-4 outline-none transition-colors duration-300"
                      data-testid="contact-form-name-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-slate-300 focus:border-[#C9A14A] focus:ring-1 focus:ring-[#C9A14A] rounded-md p-4 outline-none transition-colors duration-300"
                      data-testid="contact-form-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-slate-300 focus:border-[#C9A14A] focus:ring-1 focus:ring-[#C9A14A] rounded-md p-4 outline-none transition-colors duration-300"
                      data-testid="contact-form-phone-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="service_type" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Service Type *
                    </label>
                    <select
                      id="service_type"
                      name="service_type"
                      value={formData.service_type}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-slate-300 focus:border-[#C9A14A] focus:ring-1 focus:ring-[#C9A14A] rounded-md p-4 outline-none transition-colors duration-300"
                      data-testid="contact-form-service-select"
                    >
                      <option value="">Select a service</option>
                      {serviceTypes.map((service, index) => (
                        <option key={index} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full bg-white border border-slate-300 focus:border-[#C9A14A] focus:ring-1 focus:ring-[#C9A14A] rounded-md p-4 outline-none resize-none transition-colors duration-300"
                    data-testid="contact-form-message-textarea"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-accent px-8 py-4 rounded-md font-medium w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-form-submit-button"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="pb-0" data-testid="contact-map-section">
        <div className="w-full h-96">
          <iframe
            src="https://maps.google.com/maps?q=Shri+Balaji+Heights,+Ravi+Shankar+Marg,+Kalpataru+Nagar,+Nashik+422011&t=&z=16&ie=UTF8&iwloc=&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="RERA Hub Nashik Office - Shri Balaji Heights, Kalpataru Nagar, Nashik 422011"
            data-testid="contact-google-map"
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
